import { useEffect, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

/**
 * v0.6.1 Fase 8 — slide-in nav drawer for mobile/tablet viewports (<lg).
 *
 * Used by AdminShell, UploaderShell, and ScreeningPage to surface the
 * sidebar nav content that's normally docked on the left on desktop.
 *
 * Behavior:
 *   - Backdrop click closes the drawer.
 *   - ESC key closes the drawer.
 *   - body-scroll-lock while open (overflow:hidden on document.body).
 *   - Slide-in animation from the left (200ms cubic ease).
 *   - Visible SOLO hasta el ancho que declara quien lo monta (`upTo`), y
 *     ese mismo ancho gobierna el bloqueo de scroll. El llamador tiene que
 *     poner su disparador en EL MISMO breakpoint — si no coinciden, el
 *     boton aparece donde el cajon no se dibuja y la pagina se congela
 *     (paso de verdad: agente en `xl`, cajon en `lg`, hueco 1024-1279).
 *
 * Stateless about its own visibility — callers own the open/close state.
 * This keeps it composable with whatever button the page already has.
 */
/**
 * COLA-AGENTE A3 — hasta que ancho vive el cajon. Tailwind necesita las
 * clases ESCRITAS (no las arma en tiempo de ejecucion), asi que van a
 * mano; y la consulta de medios espeja el mismo numero para el bloqueo de
 * scroll. Los dos valores de cada fila tienen que decir lo mismo.
 */
const BP = {
  md: { cls: 'md:hidden', media: '(max-width: 767.98px)' },
  lg: { cls: 'lg:hidden', media: '(max-width: 1023.98px)' },
  xl: { cls: 'xl:hidden', media: '(max-width: 1279.98px)' },
} as const;

export interface MobileNavDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Accessibility name announced to screen readers. */
  ariaLabel: string;
  /** Optional title rendered in the drawer header. */
  title?: string;
  children: ReactNode;
  /**
   * Hasta que ancho se dibuja el cajon. Default 'lg' (la conducta de
   * siempre: admin lo monta asi y uploader mas abajo todavia). El agente
   * pasa 'xl' porque su disparador aparece por debajo de 1280.
   */
  upTo?: keyof typeof BP;
}

export function MobileNavDrawer({
  open,
  onClose,
  ariaLabel,
  title,
  children,
  upTo = 'lg',
}: MobileNavDrawerProps): JSX.Element {
  // ESC to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // body-scroll-lock while open.
  //
  // COLA-AGENTE A3 — ojo con esto: el bloqueo corria SIEMPRE que `open` era
  // true, sin mirar el ancho. Con el disparador del agente en `xl:hidden`
  // (<1280) y el cajon en `lg:hidden` (<1024), entre 1024 y 1279 el agente
  // apretaba «⋮», no veia nada, y la PAGINA DEJABA DE HACER SCROLL. Unica
  // salida: Escape (el fondo que cierra al clickear tambien estaba oculto).
  // Esa franja es donde viven las laptops de 14 pulgadas.
  //
  // Ahora el ancho lo declara quien monta el cajon (`upTo`), y el bloqueo va
  // atado a la MISMA regla: si el panel no se dibuja, no se bloquea nada.
  // Cinturon y tirantes: un breakpoint desalineado no puede volver a
  // congelar la pagina.
  useEffect(() => {
    if (!open) return;
    const mql = window.matchMedia(BP[upTo].media);
    if (!mql.matches) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, upTo]);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop — click closes. Se dibuja hasta el ancho que
              declara `upTo`, el MISMO del disparador, so any leftover state on
              desktop doesn't render a phantom overlay. */}
          {/* F5-R1a — vestido del brand (backdrop navy, hairlines, focus teal).
              API de props intacta; lo comparten admin/uploader/screening. */}
          <motion.div
            key="backdrop"
            className={`fixed inset-0 z-40 bg-nav-navy/40 backdrop-blur-sm ${BP[upTo].cls}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            aria-hidden="true"
          />
          {/* Panel — slides in from the left. */}
          <motion.aside
            key="panel"
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            className={`fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-white border-r border-nav-hair shadow-xl flex flex-col ${BP[upTo].cls} font-body text-nav-navy`}
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-nav-hair">
              <p className="font-head text-sm font-bold text-nav-navy truncate">
                {title ?? ariaLabel}
              </p>
              <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar menú"
                className="inline-flex h-11 w-11 items-center justify-center rounded-[10px] text-nav-slate hover:bg-nav-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-3 py-3">{children}</div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
